import { LinkChargebeeDocument, LinkEntry, RelinkAccount, RelinkChargebeeCustomer, RelinkReview } from '../../api/issues';

/**
 * What the relink and link dialogs compare: a Chargebee object field by field next to what Exact has. Shared by
 * the old Issues page and the v2 panel, so both judge a match the same way.
 */

export interface CompareField {
    label: string;
    chargebee?: string | null;
    exact?: string | null;
    /** Whether they match; null when there is nothing to compare. */
    match: boolean | null;
}

const STATUS: Record<string, string> = { C: 'Klant', P: 'Prospect', S: 'Suspect', A: 'Geen' };

/** What the outcome of a relink means, in the words of the screen. */
export const RELINK_OUTCOME: Record<string, string> = {
    Queued: 'Omgekoppeld en opnieuw aangeboden.',
    DryRun: 'Proefdraai: er is niets veranderd.',
    Blocked: 'Niet omgekoppeld.',
    Failed: 'Mislukt.',
};

export const LINK_OUTCOME: Record<string, string> = {
    Linked: 'Gekoppeld.',
    DryRun: 'Proefdraai: er is niets veranderd.',
};

const normal = (value?: string | null) => (value ?? '').trim().toLowerCase().replace(/\s+/g, '');

/** Whether two values say the same; null when one of them is empty and there is nothing to compare. */
const same = (left?: string | null, right?: string | null): boolean | null => (!left || !right ? null : normal(left) === normal(right));

const place = (city?: string | null, country?: string | null) => [city, country].filter(Boolean).join(', ');

export const money = (amount?: number | null, currency?: string | null) =>
    amount == null ? null : `${currency ?? 'EUR'} ${amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const day = (value?: string | null) => (value ? value.slice(0, 10) : null);

/** The fields of an account next to the Chargebee customer, the ones that tell who it is first. */
export const relinkFields = (customer: RelinkChargebeeCustomer | null | undefined, account: RelinkAccount): CompareField[] => {
    const known = customer?.exists ? customer : null;
    const nameMatch = known ? same(account.name, known.company) || same(account.name, known.name) : null;

    return [
        { label: 'Naam', chargebee: known ? known.company || known.name : null, exact: account.name, match: known && account.name ? !!nameMatch : null },
        { label: 'E-mail', chargebee: known?.email, exact: account.email, match: same(account.email, known?.email) },
        { label: 'Btw-nummer', chargebee: known?.vatNumber, exact: account.vatNumber, match: same(account.vatNumber, known?.vatNumber) },
        { label: 'Plaats', chargebee: place(known?.city, known?.country), exact: place(account.city, account.country), match: same(account.city, known?.city) },
        { label: 'Zoekcode', chargebee: known?.id, exact: account.searchCode, match: same(account.searchCode, known?.id) },
        { label: 'Code', chargebee: null, exact: account.code?.trim(), match: null },
        { label: 'Status', chargebee: null, exact: `${STATUS[account.status ?? ''] ?? account.status ?? '–'}${account.blocked ? ', geblokkeerd' : ''}`, match: null },
        { label: 'KvK', chargebee: null, exact: account.chamberOfCommerce, match: null },
        { label: 'Aangemaakt', chargebee: known?.createdAt, exact: account.created?.slice(0, 10), match: null },
    ];
};

/** How many of the telling fields match: what the list is sorted by. */
export const relinkScore = (customer: RelinkChargebeeCustomer | null | undefined, account: RelinkAccount) =>
    relinkFields(customer, account).filter((f) => f.match === true).length;

/** The candidates, the best match first. */
export const rankedCandidates = (review: RelinkReview) =>
    [...review.candidates].sort((a, b) => relinkScore(review.chargebee, b) - relinkScore(review.chargebee, a));

/**
 * The account to set the radio on: the scanner's proposal, else the best match when at least two fields agree.
 * Only a suggestion: the person still confirms it is the same customer.
 */
export const suggestedAccount = (review: RelinkReview): string | null => {
    const proposed = review.candidates.find((c) => c.id === review.proposedId);
    if (proposed) return proposed.id ?? null;
    const best = rankedCandidates(review)[0];
    return best && relinkScore(review.chargebee, best) >= 2 ? best.id ?? null : null;
};

/** The booking next to the Chargebee document, the fields that tell whether it is the same one first. */
export const linkFields = (document: LinkChargebeeDocument | null | undefined, entry: LinkEntry): CompareField[] => {
    const known = document?.exists ? document : null;
    // Exact holds the amount in the division's currency: only comparable when the document is in euros too.
    const comparable = known?.total != null && entry.amount != null && (known.currency ?? 'EUR') === 'EUR';

    return [
        {
            label: 'Bedrag',
            chargebee: money(known?.total, known?.currency),
            exact: money(entry.amount, 'EUR'),
            match: comparable ? Math.abs(Math.abs(entry.amount ?? 0) - Math.abs(known?.total ?? 0)) < 0.005 : null,
        },
        { label: 'Datum', chargebee: known?.date, exact: day(entry.date), match: known?.date && entry.date ? known.date === day(entry.date) : null },
        {
            label: 'Klant',
            chargebee: known?.customerId,
            exact: entry.accountCode ? `relatie ${entry.accountCode}` : null,
            match: entry.sameCustomer ?? null,
        },
        { label: 'Boekstuk', chargebee: null, exact: entry.entryNumber?.toString(), match: null },
        { label: 'Dagboek', chargebee: null, exact: entry.journal, match: null },
        {
            label: 'Periode',
            chargebee: null,
            exact: entry.reportingYear ? `${entry.reportingYear}/${String(entry.reportingPeriod ?? '').padStart(2, '0')}` : null,
            match: null,
        },
        { label: 'Status', chargebee: known?.status, exact: null, match: null },
    ];
};

/** The one booking that can be tied; none when there are several or the review names a problem. */
export const linkableEntry = (review: { problem?: string | null; entries: LinkEntry[] } | null) =>
    review && !review.problem && review.entries.length === 1 ? review.entries[0] : null;
